import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  makeStyles,
  tokens,
  Text,
  Select,
  Label,
  Spinner,
  Badge,
} from "@fluentui/react-components";
import { useAuth } from "../auth/TeamsAuthProvider";
import TicketList from "../components/TicketList";
import { useTickets } from "../hooks/useTickets";
import type { TicketStatus } from "../api/tickets";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  filters: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  content: {
    flex: 1,
    overflow: "auto",
  },
  spinnerWrap: {
    display: "flex",
    justifyContent: "center",
    padding: tokens.spacingVerticalXXL,
  },
});

export default function TeamQueuePage() {
  const styles = useStyles();
  const { token, workspaceId } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const teamId = searchParams.get("teamId") ?? undefined;
  const channelName = searchParams.get("channelName") ?? "Team Queue";

  const [statusFilter, setStatusFilter] = useState<TicketStatus | "">("Open");

  const { tickets, total, loading } = useTickets(token, workspaceId, {
    status: statusFilter || undefined,
    teamId,
    limit: 100,
  });

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Text size={500} weight="semibold">{channelName}</Text>
          {!loading && (
            <Badge appearance="filled" color="brand" size="small">{total}</Badge>
          )}
        </div>
        <div className={styles.filters}>
          <Label htmlFor="status-select" size="small">Status</Label>
          <Select
            id="status-select"
            size="small"
            value={statusFilter}
            onChange={(_, d) => setStatusFilter(d.value as TicketStatus | "")}
          >
            <option value="Open">Open</option>
            <option value="Pending">Pending</option>
            <option value="Resolved">Resolved</option>
            <option value="">All</option>
          </Select>
        </div>
      </div>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.spinnerWrap}>
            <Spinner />
          </div>
        ) : (
          <TicketList
            tickets={tickets}
            onSelect={(t) => navigate(`/ticket/${t.id}`)}
          />
        )}
      </div>
    </div>
  );
}
